import React, { useEffect, useState } from "react";
import { Container } from "reactstrap";
import { Link, NavLink, useHistory } from "react-router-dom";
import { useCollectorPwa } from "../../helpers/useCollectorPwa";
import { collectorLoginPath, getCollectorRefreshToken, getCollectorToken } from "../../helpers/collectorSession";
import { renewCollectorSession } from "../../services/collectorApi";
import { isMediaOptimizationEnabled } from "../../media/mediaApi";
import { resumeCollectorArchives } from "../../media/collectorMediaUpload";
import "./my-collection.scss";

type Props = {
  children: React.ReactNode;
};

const NAV = [
  { to: "/my-collection", label: "My pieces", exact: true },
  { to: "/my-collection/scanned", label: "Scanned", exact: true },
  { to: "/my-collection/tags", label: "My tags", exact: true },
  { to: "/my-collection/help", label: "Help", exact: true },
  { to: "/my-collection/settings", label: "Settings", exact: true },
];

const MyCollectionLayout: React.FC<Props> = ({ children }) => {
  useCollectorPwa();
  const [menuOpen, setMenuOpen] = useState(false);

  const history = useHistory();

  // Photo originals left archiving when the page closed carry on in the background. Once per account per tab;
  // leaving a page does not stop them (only sign-out does, in Settings).
  useEffect(() => {
    if (isMediaOptimizationEnabled() && getCollectorToken()) resumeCollectorArchives();
  }, []);

  // Sessions saved before refresh tokens were kept try to pick one up on the next visit. If the server
  // will not renew that old token, it is kept and used until a real request is refused with it; only
  // then is it cleared and the person sent to sign in (and back to this page). A refusal is remembered
  // per tab for that token (collectorApi), so later page loads do not ask again.
  useEffect(() => {
    if (getCollectorToken() && !getCollectorRefreshToken()) {
      renewCollectorSession().then((ok) => {
        if (!ok && !getCollectorToken()) {
          history.replace(collectorLoginPath(history.location.pathname + history.location.search));
        }
      });
    }
  }, [history]);

  return (
    <div className="my-collection-layout">
      <a href="#my-collection-main" className="visually-hidden-focusable">
        Skip to content
      </a>
      <header className="mc-app-header">
        <Container>
          <div className="mc-app-header-inner">
            <Link to="/my-collection" className="mc-wordmark">
              <span className="sp1">Hastkala</span>{" "}
              <span className="sp2">Paramanak</span>
              <span className="mc-app-name">My Collection</span>
            </Link>
            <button
              type="button"
              className="mc-nav-toggle"
              aria-expanded={menuOpen}
              aria-controls="mc-app-nav"
              onClick={() => setMenuOpen((open) => !open)}
            >
              Menu
            </button>
            <nav
              id="mc-app-nav"
              className={`mc-nav ${menuOpen ? "is-open" : ""}`}
              aria-label="My Collection"
            >
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  exact={item.exact}
                  className="mc-nav-link"
                  activeClassName="is-active"
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </Container>
      </header>
      <Container>
        <main id="my-collection-main" className="mc-page pt-3">
          {children}
        </main>
      </Container>
    </div>
  );
};

export default MyCollectionLayout;

